import { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FaRobot, FaTimes, FaPaperPlane, FaSpinner, FaLightbulb, FaGraduationCap, FaBriefcase } from 'react-icons/fa';

const AIAssistant = () => {
  const { darkMode } = useSelector((state) => state.ui);
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: 1, text: 'Hello! I\'m your AI learning assistant. How can I help you today?', sender: 'ai' }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Sample suggestions for the AI assistant
  const suggestions = [
    { id: 1, text: 'Help me understand React hooks', icon: <FaLightbulb /> },
    { id: 2, text: 'Recommend courses for web development', icon: <FaGraduationCap /> },
    { id: 3, text: 'Career paths in data science', icon: <FaBriefcase /> }
  ];

  // Scroll to bottom of messages
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    // Add user message
    const userMessage = { id: messages.length + 1, text: inputValue, sender: 'user' };
    setMessages([...messages, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Simulate AI response after a delay
    setTimeout(() => {
      const aiResponse = generateAIResponse(inputValue);
      setMessages(prevMessages => [...prevMessages, { 
        id: prevMessages.length + 1, 
        text: aiResponse, 
        sender: 'ai' 
      }]);
      setIsTyping(false);
    }, 1500);
  };

  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion.text);
    handleSubmit({ preventDefault: () => {} });
  };

  // Simple AI response generator (would be replaced with actual API call)
  const generateAIResponse = (userInput) => {
    const input = userInput.toLowerCase();
    
    if (input.includes('react') && input.includes('hooks')) {
      return 'React Hooks are functions that let you "hook into" React state and lifecycle features from function components. The most commonly used hooks are useState, useEffect, and useContext. Would you like me to explain any specific hook in more detail?';
    } else if (input.includes('recommend') && input.includes('course')) {
      return 'Based on your learning history, I recommend checking out "Advanced JavaScript Patterns" and "React Performance Optimization". These courses align with your recent interests in frontend development.';
    } else if (input.includes('career') && input.includes('data science')) {
      return 'Data Science offers various career paths including Data Analyst, Data Engineer, Machine Learning Engineer, and Data Scientist. Based on your current skills, focusing on Data Analysis fundamentals would be a good starting point. Would you like me to suggest some courses to begin with?';
    } else {
      return 'I\'m here to help with your learning journey. You can ask me about course recommendations, assignment help, or career guidance. What specific area are you interested in?';
    }
  };

  return (
    <>
      {/* Chat button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 bg-primary text-white p-4 rounded-full shadow-lg hover:bg-primary-dark transition-colors z-50"
        aria-label="Open AI Assistant"
      >
        <FaRobot className="text-xl" />
      </button>

      {/* Chat window */}
      {isOpen && (
        <div className={`fixed bottom-24 right-6 w-96 h-[500px] container-rounded-xl shadow-2xl flex flex-col z-50 border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          {/* Header */}
          <div className={`flex items-center justify-between p-4 ${darkMode ? 'border-b border-gray-700' : 'border-b'}`}>
            <div className="flex items-center">
              <div className="bg-primary text-white p-2 rounded-full mr-3">
                <FaRobot />
              </div>
              <div>
                <h3 className={`font-medium ${darkMode ? 'text-white' : ''}`}>AI Learning Assistant</h3>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Always here to help</p>
              </div>
            </div>
            <button 
              onClick={() => setIsOpen(false)}
              className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
              aria-label="Close AI Assistant"
            >
              <FaTimes />
            </button>
          </div>

          {/* Messages */}
          <div className={`flex-1 p-4 overflow-y-auto ${darkMode ? 'bg-gray-800' : ''}`}>
            <div className="space-y-4">
              {messages.map((message) => (
                <div 
                  key={message.id} 
                  className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div 
                    className={`max-w-[80%] p-3 container-rounded-lg ${message.sender === 'user' 
                      ? 'bg-primary text-white rounded-tr-none' 
                      : darkMode 
                        ? 'bg-gray-700 text-gray-100 rounded-tl-none' 
                        : 'bg-gray-100 text-gray-800 rounded-tl-none'}`}
                  >
                    <p className="text-sm">{message.text}</p>
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="flex justify-start">
                  <div className={`p-3 container-rounded-lg rounded-tl-none max-w-[80%] ${darkMode ? 'bg-gray-700 text-gray-100' : 'bg-gray-100 text-gray-800'}`}>
                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 rounded-full animate-bounce ${darkMode ? 'bg-gray-400' : 'bg-gray-400'}`}></div>
                      <div className={`w-2 h-2 rounded-full animate-bounce ${darkMode ? 'bg-gray-400' : 'bg-gray-400'}`} style={{ animationDelay: '0.2s' }}></div>
                      <div className={`w-2 h-2 rounded-full animate-bounce ${darkMode ? 'bg-gray-400' : 'bg-gray-400'}`} style={{ animationDelay: '0.4s' }}></div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Suggestions */}
          {messages.length < 3 && (
            <div className={`px-4 py-2 ${darkMode ? 'border-t border-gray-700' : 'border-t border-gray-100'}`}>
              <p className={`text-xs mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Suggested questions:</p>
              <div className="flex flex-wrap gap-2">
                {suggestions.map(suggestion => (
                  <button
                    key={suggestion.id}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className={`flex items-center text-xs px-3 py-1.5 rounded-full transition-colors ${darkMode 
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                  >
                    <span className="mr-1.5">{suggestion.icon}</span>
                    {suggestion.text}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <form onSubmit={handleSubmit} className={`p-4 ${darkMode ? 'border-t border-gray-700' : 'border-t'}`}>
            <div className="flex items-center">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Ask me anything..."
                className={`flex-1 p-2 container-rounded-l-lg focus:outline-none focus:ring-1 focus:ring-primary ${darkMode 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                  : 'border'}`}
              />
              <button
                type="submit"
                disabled={!inputValue.trim() || isTyping}
                className={`p-2 container-rounded-r-lg ${!inputValue.trim() || isTyping 
                  ? 'bg-gray-300 text-gray-500' 
                  : 'bg-primary text-white hover:bg-primary-dark'} transition-colors`}
              >
                {isTyping ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default AIAssistant;